import { useNavigate, useParams } from 'react-router';
import { useAnalysisQuery } from '../api/queries/analysis';
import { useErrorToast } from '../lib/useErrorToast';
import { AnalysisValues } from '../components/analysis-value/analysis-values';
import { Button, Delete, Tabs } from '@convergence/components';
import { useLaunchAnalysisMutation } from '../api/queries/launchAnalysis';
import { StatusIndicator } from '../components/StatusIndicator';
import { AnalysisValueProps } from '../components/analysis-value/types';
import { useCancelAnalysisMutation } from '../api/queries/cancelAnalysis';
import { AnalysisNext } from '../components/AnalysisNext';
import { Tasks } from '../components/tasks/Tasks';
import { AnalysisLogs } from '../components/Logs';
import { AnalysisDtoType } from '@convergence/client';
import { useStateUrlSimple } from '../hooks/useStateUrlSimple';
import { useEffect } from 'react';
import { Cost } from '../components/Cost';
import { useDeleteAnalysisMutation } from '../api/mutations/deleteAnalysis';

export function Analysis() {
  const params = useParams();
  const navigate = useNavigate();
  const slug = params.slug;
  const analysisQuery = useAnalysisQuery(slug);
  const [tab, setTab] = useStateUrlSimple('tab', 'results');
  const deleteAnalysisMutation = useDeleteAnalysisMutation();

  useEffect(() => {
    if (!params.slug) {
      navigate('/analyses');
    }
  });

  const launchAnalysisMutation = useLaunchAnalysisMutation();
  const cancelAnalysisMutation = useCancelAnalysisMutation();

  useErrorToast(analysisQuery.error);

  const status = analysisQuery.data?.status ?? 'PENDING';

  const inputs = analysisQuery.data?.input;
  const outputs = analysisQuery.data?.output;

  if (analysisQuery.isLoading) {
    return (
      <div className="h-full flex items-center justify-center">Loading...</div>
    );
  }

  if (!slug) {
    return null;
  }

  return (
    <div className="h-full flex flex-col flex-grow overflow-hidden bg-white">
      <header className="flex items-center justify-between border-b mb-4 border-black p-4">
        <div className="flex items-center gap-1">
          <StatusIndicator
            status={status}
            statuses={{
              PENDING: {
                color: '#ffc107',
                pulse: true
              },
              COMPLETED: {
                color: '#28a745'
              },
              FAILED: {
                color: '#dc3545'
              },
              CANCELLED: {
                color: '#dc3545'
              }
            }}
          />
          {status === 'CANCELLED' && ` (Cancelled)`}
          <h1 className="text-xl font-terminal">
            {analysisQuery.data?.name ?? ''}
          </h1>
        </div>
        {status === 'PENDING' ? (
          <Button
            onClick={() => {
              if (!analysisQuery.data) return;
              cancelAnalysisMutation.mutate({
                analysisId: analysisQuery.data.id
              });
            }}
          >
            Cancel
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              onClick={() => {
                if (!analysisQuery.data) return;
                launchAnalysisMutation.mutate({
                  analysisId: analysisQuery.data.id,
                  analysisSlug: analysisQuery.data.slug
                });
              }}
            >
              Relaunch
            </Button>
            <Delete
              onDelete={async () => {
                try {
                  const response =
                    await deleteAnalysisMutation.mutateAsync(slug);
                  if (response.success) {
                    navigate('/a');
                  }
                } catch (e) {
                  console.error('Failed to delete analysis', e);
                }
              }}
            ></Delete>
          </div>
        )}
      </header>
      <div className="h-full bg-white flex-grow overflow-hidden p-2">
        <Tabs
          onChange={(t) => {
            setTab(t);
          }}
          activeTab={tab as 'results' | 'tasks' | 'logs' | 'cost'}
          items={[
            { label: 'Results', value: 'results' },
            { label: 'Tasks', value: 'tasks' },
            { label: 'Logs', value: 'logs' },
            { label: 'Cost', value: 'cost' }
          ]}
        >
          <Tabs.Tab value={'results'}>
            <Results inputs={inputs} outputs={outputs} />
          </Tabs.Tab>
          <Tabs.Tab value={'tasks'}>
            <Tasks slug={slug} />
          </Tabs.Tab>
          <Tabs.Tab value={'logs'}>
            <AnalysisLogs slug={slug} />
          </Tabs.Tab>
          <Tabs.Tab value={'cost'}>
            <Cost slug={slug} />
          </Tabs.Tab>
        </Tabs>
      </div>
      <AnalysisNext slug={slug} />
    </div>
  );
}

interface ResultsProps {
  inputs?: AnalysisDtoType['input'];
  outputs?: AnalysisDtoType['output'];
}

function Results({ inputs, outputs }: ResultsProps) {
  return (
    <div className="p-4 overflow-scroll h-full flex-grow">
      {inputs && (
        <AnalysisValues values={inputs as AnalysisValueProps['value']} />
      )}
      {outputs ? (
        <AnalysisValues values={outputs as AnalysisValueProps['value']} />
      ) : (
        <div>Outputs not available yet</div>
      )}
    </div>
  );
}
